import React from "react";
import { fromDate } from "@internationalized/date";
import { ArrowLeft, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@workspace/ui/components/button";
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@workspace/ui/components/card";
import { FormattedDate } from "@workspace/ui/components/formatted-date";
import type { TimeOnly } from "@workspace/ui/lib/time-only";
import { getTimeOnlyDifference } from "@workspace/ui/lib/time-only";

import type { Shift, StaffMember } from "../types";
import {
	EarlyLeaveReasonPicker,
	type EarlyLeaveChoice,
} from "./early-leave-reason-picker";
import { StaffLoginForm } from "./staff-login-form";
import { StaffPinResetForm } from "./staff-pin-reset-form";
import { useCheckInOut } from "./use-check-in-out";

interface CheckInOutProps {
	staffMembers: StaffMember[];
	shifts: Shift[];
	onClose: () => void;
	venueId: string;
	tenant: string;
	timeZone: string;
}

function Duration({
	startTime,
	endTime,
}: {
	startTime: TimeOnly;
	endTime: TimeOnly | null | undefined;
}) {
	if (!endTime) return null;

	const diff = getTimeOnlyDifference(startTime, endTime);

	return (
		<span>
			{diff.hours > 0 && `${diff.hours} hours `}
			{diff.hours > 0 && diff.minutes > 0 && ` and `}
			{diff.minutes > 0 && `${diff.minutes} minutes`}
		</span>
	);
}

export function CheckInOutPinOnly({
	staffMembers,
	shifts,
	onClose: oC,
	venueId,
	tenant,
	timeZone,
}: CheckInOutProps) {
	const {
		showSuccess,
		showCheckoutSuccess,
		shouldShake,
		handleVerifyPinAndChangeStatus,
		errorMessage: hasError,
		currentShift,
		status,
		checkout,
		setPin: resetPin,
		reset,
		selectedStaff,
	} = useCheckInOut({
		staffMembers,
		shifts,
		venueId,
		tenant,
		timeZone,
	});

	const timeout = React.useRef<number>(null);

	function onClose() {
		oC();
		reset();
		if (timeout.current) {
			clearTimeout(timeout.current);
			timeout.current = null;
		}
	}

	React.useEffect(() => {
		if (!hasError) return;

		toast.error(hasError || "An error occurred. Please try again.");
	}, [hasError]);

	React.useEffect(() => {
		return () => {
			if (timeout.current) {
				clearTimeout(timeout.current);
				timeout.current = null;
			}
		};
	}, []);

	const handleVerifyPin = async (data: { staffId: string; pin: string }) => {
		try {
			const result = await handleVerifyPinAndChangeStatus(data);

			if (result) {
				timeout.current = setTimeout(() => {
					onClose();
				}, 5000) as unknown as number;
			}

			return result;
		} catch {
			return false;
		}
	};

	async function handlePinSet(data: { newPin: string; confirmPin: string }) {
		const result = await resetPin(data);

		if (!selectedStaff) return result;

		if (result) {
			return await handleVerifyPin({
				staffId: selectedStaff.staffNumber.toString(),
				pin: data.newPin,
			});
		}

		return result;
	}

	const handleBack = () => {};

	async function handleCheckoutEarly(choice: EarlyLeaveChoice) {
		// HA-368: the server records the time the shift was left.
		const result = await checkout(choice);

		if (result) {
			timeout.current = setTimeout(() => {
				onClose();
			}, 5000) as unknown as number;
		}

		return result;
	}

	function cancel() {
		onClose();
	}

	const showPin =
		status === "idle" || status === "checking-pin" || status === "error";
	// !showSuccess && !showCheckoutSuccess && status !== "checkout-early" && status !== "pin-reset-required";
	const now = fromDate(new Date(), timeZone);

	return (
		<>
			<Card
				className={`mx-auto w-full max-w-md transition-transform duration-150 ${
					shouldShake ? " animate-shake" : ""
				}`}
			>
				{status !== "pin-reset-required" && (
					<>
						<CardHeader className="relative">
							<CardTitle className=" flex flex-row justify-between">
								<div className="flex items-center">
									<Button
										variant="ghost"
										size="icon"
										onClick={() => {
											handleBack();
											onClose();
										}}
										className="mr-2"
									>
										<ArrowLeft className="size-4" />
									</Button>
									Staff Check In/Out
								</div>
								<Button variant="ghost" size="icon" onClick={onClose}>
									<X className="size-4" />
								</Button>
							</CardTitle>
						</CardHeader>
					</>
				)}

				<CardContent>
					{showPin && (
						<StaffLoginForm onSubmit={handleVerifyPin} onCancel={cancel} />
					)}

					{status === "checkout-early" && currentShift && (
						<div className=" space-y-4 text-center text-xl">
							<div>
								You are checking out{" "}
								<Duration
									endTime={`${now.hour}:${now.minute}` as TimeOnly}
									startTime={currentShift.endTime as TimeOnly}
								/>{" "}
								early.
							</div>
							<EarlyLeaveReasonPicker onSubmit={handleCheckoutEarly} />
						</div>
					)}
					{showSuccess && (
						<div className="py-8 text-center">
							<h3 className="mb-2 text-xl font-semibold">Success!</h3>

							<p className="text-muted-foreground">
								Checked in at{" "}
								<FormattedDate
									date={new Date()}
									options={{
										hour: "2-digit",
										minute: "2-digit",
										second: "2-digit",
										hour12: true,
									}}
									timeZone={timeZone}
								/>
							</p>
						</div>
					)}

					{showCheckoutSuccess && (
						<div className="py-8 text-center">
							<h3 className="mb-2 text-xl font-semibold">Success!</h3>

							<p className="text-muted-foreground">
								Checked out at{" "}
								<FormattedDate
									date={new Date()}
									options={{
										hour: "2-digit",
										minute: "2-digit",
										second: "2-digit",
										hour12: true,
									}}
									timeZone={timeZone}
								/>
							</p>
						</div>
					)}

					{status === "pin-reset-required" && (
						<StaffPinResetForm onSubmit={handlePinSet} onCancel={reset} />
					)}
				</CardContent>

				{(showSuccess || showCheckoutSuccess) && (
					<CardFooter className="flex justify-center">
						<Button onClick={onClose}>Done</Button>
					</CardFooter>
				)}
			</Card>
		</>
	);
}
